import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEmployee } from 'context/EmployeeContext';
import { useQueries } from '@tanstack/react-query';
import { getEmployeeProfile, getCompanyPayrolls, previewPayroll, getEmployeeDocuments } from 'services/employeePortal';
import routes from 'constants/routes';

const StatusBadge = ({ status, type }) => {
    const colors = {
        onboard: {
            completed: { bg: '#28a745', label: 'Completed' },
            active:    { bg: '#28a745', label: 'Completed' },
            pending:   { bg: '#ffc107', label: 'Pending' },
            'n/a':     { bg: '#6c757d', label: 'N/A' },
        },
        active: {
            active:   { bg: '#28a745', label: 'Active' },
            inactive: { bg: '#ffc107', label: 'Inactive' },
        },
    };
    const map = type === 'onboard' ? colors.onboard : colors.active;
    const key = (status || 'n/a').toLowerCase();
    const conf = map[key] || { bg: '#6c757d', label: status || 'N/A' };
    return (
        <span style={{
            backgroundColor: conf.bg,
            color: '#fff',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 500,
        }}>
            {conf.label}
        </span>
    );
};

const EmployeeDashboard = () => {
    const navigate = useNavigate();
    const { employeeId } = useEmployee();

    const { data: employee } = useQuery({
        queryKey: ['ep-employee', employeeId],
        queryFn: () => getEmployeeProfile(employeeId),
        enabled: !!employeeId,
    });

    const { data: allPayrolls = [] } = useQuery({
        queryKey: ['ep-company-payrolls'],
        queryFn: getCompanyPayrolls,
        enabled: !!employeeId,
    });

    const approvedPayrolls = useMemo(() => allPayrolls.filter(p => p.status === 'approved'), [allPayrolls]);

    const previewQueries = useQueries({
        queries: approvedPayrolls.map(p => ({
            queryKey: ['ep-preview', p.id],
            queryFn: () => previewPayroll(p.id),
            enabled: !!employeeId,
            staleTime: 1000 * 60 * 10,
        })),
    });

    const paystubs = useMemo(() => {
        if (!employeeId) return [];
        return approvedPayrolls.filter((_, i) => {
            const preview = previewQueries[i]?.data;
            if (!preview?.employees) return false;
            return preview.employees.some(e =>
                (e.employeeId === employeeId || e.id === employeeId) &&
                (e.grossPay ?? 0) > 0
            );
        });
    }, [approvedPayrolls, previewQueries, employeeId]);

    const { data: documents = [] } = useQuery({
        queryKey: ['ep-documents', employeeId],
        queryFn: () => getEmployeeDocuments(employeeId),
        enabled: !!employeeId,
    });

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h2 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Dashboard</h2>
            <p style={{ color: '#6c757d', marginBottom: 24 }}>
                Access your payroll information, manage benefits, and stay connected – all in one place.
            </p>

            {/* Status Card */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', marginBottom: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 14 }}>Onboard Status</p>
                    <StatusBadge status={employee?.onboardStatus || employee?.status || 'pending'} type="onboard" />
                </div>
                <div>
                    <p style={{ color: '#6c757d', marginBottom: 8, fontSize: 14 }}>Active Status</p>
                    <StatusBadge status={employee?.active ? 'active' : 'inactive'} type="active" />
                </div>
                <button
                    onClick={() => navigate(`${routes.EMPLOYEE_PORTAL.HOME}${routes.EMPLOYEE_PORTAL.PROFILE}`)}
                    style={{ background: '#fff', border: '1px solid #e87722', color: '#e87722', borderRadius: 8, padding: '8px 20px', fontWeight: 600, cursor: 'pointer' }}
                >
                    View Profile
                </button>
            </div>

            {/* Paystubs Table */}
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 16 }}>Paystubs</h4>
            <div style={{ background: '#fff', borderRadius: 10, marginBottom: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                    <thead>
                        <tr style={{ background: '#f0f9fb' }}>
                            {['Payday', 'Period', 'Net Pay', 'Gross Pay', 'Total Taxes', 'Deductions', 'Action'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paystubs.length === 0 ? (
                            <tr><td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>No paystubs found</td></tr>
                        ) : paystubs.slice(0, 5).map((p, i) => {
                            const preview = previewQueries[approvedPayrolls.indexOf(p)]?.data;
                            const empData = preview?.employees?.find(e => e.employeeId === employeeId || e.id === employeeId);
                            const netPay = empData?.netPay ?? empData?.net_pay ?? p.netPay ?? 0;
                            const grossPay = empData?.grossPay ?? empData?.gross_pay ?? p.grossPay ?? 0;
                            const taxes = empData?.employeeTaxes ?? empData?.totalTaxes ?? p.totalTaxes ?? 0;
                            const deductions = empData?.totalDeductions ?? p.totalDeductions ?? 0;
                            const fmt = (v) => `$${Number(v).toFixed(2)}`;
                            const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' }) : '—';
                            return (
                                <tr key={p.id || i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.payDate)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.startDate)} – {fmtDate(p.endDate)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(netPay)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(grossPay)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(taxes)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(deductions)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                        <button onClick={() => navigate(`${routes.EMPLOYEE_PORTAL.HOME}${routes.EMPLOYEE_PORTAL.PAYSTUBS}`)} style={{ background: 'none', border: 'none', color: '#1a9fbb', cursor: 'pointer', fontWeight: 500 }}>View</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
                <div style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: '#6c757d' }}>
                    Showing latest 5 of {paystubs.length} paystub{paystubs.length !== 1 ? 's' : ''}
                </div>
            </div>

            {/* Documents Table */}
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 16 }}>Documents</h4>
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                    <thead>
                        <tr style={{ background: '#f0f9fb' }}>
                            {['Label', 'Jurisdiction', 'Category', 'Year', 'Filed On', 'Action'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {documents.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>No documents found</td></tr>
                        ) : documents.slice(0, 5).map((d, i) => (
                            <tr key={d.id || i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.label || d.name || '-'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.jurisdiction || '-'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.category || d.type || '-'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.year || '-'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>{d.filedAt || d.filed_at || d.createdAt ? new Date(d.filedAt || d.filed_at || d.createdAt).toLocaleDateString() : '-'}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                    <button onClick={() => navigate(`${routes.EMPLOYEE_PORTAL.HOME}${routes.EMPLOYEE_PORTAL.DOCUMENTS}`)} style={{ background: 'none', border: 'none', color: '#1a9fbb', cursor: 'pointer', fontWeight: 500 }}>View</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <div style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: '#6c757d' }}>
                    Showing latest 5 of {documents.length} document{documents.length !== 1 ? 's' : ''}
                </div>
            </div>
        </div>
    );
};

export default EmployeeDashboard;
